const ONR_AB_EXPERIMENT = 'onr-cart-ab';
// types
import { CartItem, CartTaggedCoupon, Cart } from 'shared/types/cart';
import { CartListElements, CartPageElement, CartPageLoadData } from './types';

// helpers
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';
import { buildStoreRedirectId } from 'shared/helpers/storeUrl';

// constant
const LOCAL_STORAGE_KEY = 'nykaa_show_onr';
const LOCAL_STORAGE_KEY_EDD = 'isEddV2Enabled';
import { BEST_COUPON_STATUS } from 'shared/layouts/Cart/constants';

export const setOnrKeyStorage = () => {
  const { isEligible = false } = getEligibleVariantName(ONR_AB_EXPERIMENT);
  try {
    if (window.sessionStorage === undefined) {
      return;
    }
    window.sessionStorage.setItem(LOCAL_STORAGE_KEY, isEligible ? 'true' : 'false');
  } catch (e) {
    /** DO NOTHING */
  }
};

export const getOnrKeyStorage = () => {
  try {
    if (window.sessionStorage === undefined) {
      return;
    }
    return window.sessionStorage.getItem(LOCAL_STORAGE_KEY) === 'true' || false;
  } catch (e) {
    return false;
  }
};

// is product partially available
export const isPartialOOS = (item: CartItem) => {
  if (item.quantity > 1) {
    if (item.availableQty < item.quantity) return true;
  }
  return false;
};

export const getCheckUnCheckCounter = (): number => {
  return parseInt(window.sessionStorage.getItem('checkUnCheckCounter') || '0', 10);
};



const getBestCouponStatus = (
  taggedCoupon: CartTaggedCoupon | undefined,
  cartCouponCode: string
): string => {
  if (!taggedCoupon) return BEST_COUPON_STATUS.NOT_APPLICABLE;
  if (cartCouponCode && cartCouponCode === taggedCoupon.couponCode) return BEST_COUPON_STATUS.APPLIED;
  if (taggedCoupon.expiryEpoch && taggedCoupon.expiryEpoch * 1000 < Date.now()) return BEST_COUPON_STATUS.EXPIRED;
  if (taggedCoupon.state === 'EXPIRING') return BEST_COUPON_STATUS.EXPIRING;
  return BEST_COUPON_STATUS.APPLICABLE;
};

export const getBestCouponForAnalytics = (
  cart?: Pick<Cart, 'taggedCoupons' | 'appliedTaggedCoupon'>
): CartTaggedCoupon | undefined => {
  const suggestion = cart?.taggedCoupons?.[0];
  if (suggestion) return suggestion;
  return cart?.appliedTaggedCoupon ?? undefined;
};

// push element to an array only if the element is valid
const pushIfValid = (arr: string[], element?: any) => {
  if (!element) return arr;

  arr.push(element);
  return arr;
};

const getListToString = (cartListElements: { [key: string]: string[]; }) => {
  const transformData: { [key: string]: string; } = {};

  Object.keys(cartListElements).forEach((listName: string) => {
    transformData[listName] = cartListElements[listName].join(',');
  });

  return transformData;
};

export const getCartPageLoadData = (cart: CartPageLoadData) => {
  try {
    const { items = [], isEligibleOfferNudge, unselectedItems = [] } = cart;

    const cartElements: CartPageElement = {
      cartOOSCount: 0,
      productWithOfferCount: 0,
      availableOffersCount: 0,
      cartPartialOOSCount: 0,
      cartInStockCount: 0,
      cartDiscountApplied: 0,
      cartProductCount: 0,
      cartSubTotalAmount: 0,
      cartTotalAmount: 0,
      cartTotalQty: 0,
      currency: '',
      pincode: '',
      rewardPointsApplied: 0,
      shippingCharges: 0,
      additionalDiscountApplied: 0,
      cartFinalPrice: 0,
      isFomoTimerVisible: false,
      cartCouponDiscount: 0,
      isEligibleForSpeculationRule: false,
      isPopupShown: false,
      noOfOffersApplied: 0,
      checkboxEnabledOnCart: false,
      checkboxTotalCount: 0,
      checkboxSelectedCount: 0,
      checkboxUnselectedCount: 0,
      partial_checkout: false,
      salePriceLiveFlag: false,
      best_coupon_visible: false,
      best_coupon_eligible: false,
      best_coupon_tag: 'UNCATEGORIZED',
      best_coupon_code: '',
      best_coupon_status: BEST_COUPON_STATUS.NOT_APPLICABLE,
      payment_offer: false,
      payment_offer_count: 0,
      payment_meta_info: [],
    };

    const cartListElements: CartListElements = {
      brandNameList: [],
      productIdList: [],
      productNameList: [],
      variantProductIdList: [],
      productTypeList: [],
      categoryLevel1List: [],
      categoryLevel2List: [],
      categoryLevel3List: [],
      categoryLevel4List: [],
      cartAvailableOffers: [],
      cartAppliedOffers: [],
      cartProducts: [],
    };

    let luxeItemCount = 0;
    let totalIteratedItems = 0;

    cartElements.cartDiscountApplied = cart?.cartTotal?.cartDiscount?.amount;
    cartElements.cartProductCount = cart?.itemCount;
    cartElements.cartSubTotalAmount = cart?.cartTotal?.subTotal?.amount;
    cartElements.cartTotalAmount = cart?.cartTotal?.totalMrp?.amount;
    cartElements.cartTotalQty = cart?.quantity;
    cartElements.currency = cart?.currencyCode;
    cartElements.pincode = cart?.pincode || cart?.address?.postcode;
    cartElements.rewardPointsApplied = cart?.rewardsDTO?.rewardsApplied;
    cartElements.shippingCharges = cart?.cartTotal?.shipping.amount;
    cartElements.additionalDiscountApplied = cart?.cartTotal?.ruleDiscountWithoutLabel?.amount;
    cartElements.cartFinalPrice = cart?.cartTotal?.grandTotal.amount;
    if (cart?.couponCode) {
      cartElements.couponCodeApplied = cart.couponCode;
    }
    cartElements.isFomoTimerVisible = cart?.isFomoTimerVisibleOncart;
    cartElements.isEligibleForSpeculationRule = cart?.isEligibleForSpeculationRule;
    cartElements.youPayCount = cart?.youPayCount;
    cartElements.eligibleFreeSamples = cart?.freeGiftsBannerData?.eligibleCount;
    cartElements.claimedFreeSamples = cart?.freeGiftsBannerData?.claimedCount;
    cartElements.cartCouponDiscount = cart?.cartTotal?.couponDiscount?.amount;
    cartElements.noOfOffersApplied = cart?.appliedOfferLabelDetails?.length || 0;
    cartElements.checkboxEnabledOnCart = cart?.isEligibleCartPartialCheckout;

    cartElements.checkboxSelectedCount = cart?.isEligibleCartPartialCheckout ? 0 : 'na';
    cartElements.checkboxUnselectedCount = cart?.isEligibleCartPartialCheckout ? 0 : 'na';

    cartElements.isPopupShown =
      isEligibleOfferNudge && cart?.cartTotal?.promotionalDiscount?.amount > 0;
    cartElements.partial_checkout = cart?.unselectedItems?.length == 0 ? false : true;
    if (cart?.shippingDataDTO?.convenienceChargesDTO) {
      cart.shippingDataDTO.convenienceChargesDTO.map((val) => {
        cartElements[val.analyticsKey] = val.amount;
      });
    }

    // best coupon widget properties
    const taggedCoupon = getBestCouponForAnalytics(cart);
    const paymentRules = cart?.paymentMethodOffersDTO?.paymentRules ?? [];

    cartElements.best_coupon_eligible = Boolean(taggedCoupon);
    cartElements.best_coupon_visible = Boolean(taggedCoupon) && Boolean(cart.bestCouponEnabled);
    cartElements.best_coupon_tag = taggedCoupon?.tag ?? 'UNCATEGORIZED';
    cartElements.best_coupon_code = taggedCoupon?.couponCode ?? '';
    cartElements.best_coupon_status = getBestCouponStatus(taggedCoupon, cart?.couponCode ?? '');

    // payment offer properties
    cartElements.payment_offer = paymentRules.length > 0;
    cartElements.payment_offer_count = paymentRules.length;
    cartElements.payment_meta_info = paymentRules
      .map((rule) => `${rule.offerTitle || rule.name}:${rule.discountAmount}`);

    const totalItems = items;

    const n = totalItems.length || 10;
    const isOnSalePresentInTopN = totalItems?.slice(0, n).some(product => product?.onSale === true);
    cartElements.salePriceLiveFlag = totalItems.some(obj => obj.onSale === true);
    let isSpecialTagsPresent = false;
    const specialTagsNameList: string[] = [];

    totalItems.forEach((item) => {
      const appliedOffers = item.offerCommunication?.appliedOfferIds || [];
      const offers = item.offerCommunication?.availableOfferIds || [];
      const offersCount = offers.length;
      const luxeStoreId = buildStoreRedirectId('luxe');
      const isLuxeItem = item?.trackingParam?.store?.includes(luxeStoreId) ?? false;
      if (isLuxeItem) luxeItemCount++;
      totalIteratedItems++;

      if (item?.specialTag && Object.keys(item?.specialTag).length > 0) {
        isSpecialTagsPresent = true;
        const tagType = item?.specialTag.type;
        tagType && specialTagsNameList.push(tagType);
      }

      // this is added to support for future, currently we are not getting this value
      const categories = item?.primary_categories_json_all || undefined;

      cartElements.availableOffersCount += offersCount;
      cartElements.productWithOfferCount += offersCount > 0 ? 1 : 0;
      cartElements.cartOOSCount += item.isOutOfStock ? 1 : 0;
      cartElements.cartPartialOOSCount += isPartialOOS(item) ? 1 : 0;
      cartElements.cartInStockCount += item.isOutOfStock || isPartialOOS(item) ? 0 : 1;
      if (cart?.isEligibleCartPartialCheckout) {
        cartElements.checkboxSelectedCount +=
          item.isOutOfStock || isPartialOOS(item) ? 0 : item?.selectedItemForCheckout ? 1 : 0;
        cartElements.checkboxTotalCount += item.isOutOfStock || isPartialOOS(item) ? 0 : 1;
      }
      cartListElements.brandNameList = pushIfValid(
        cartListElements.brandNameList,
        item?.brandNames[0]
      );
      cartListElements.productIdList.push(item.parentId);
      cartListElements.productNameList.push(item.name);
      cartListElements.variantProductIdList.push(item.productId);

      cartListElements.productTypeList = pushIfValid(
        cartListElements.productTypeList,
        item.productType
      );
      cartListElements.categoryLevel1List = pushIfValid(
        cartListElements.categoryLevel1List,
        categories?.l1_Category?.name
      );
      cartListElements.categoryLevel2List = pushIfValid(
        cartListElements.categoryLevel2List,
        categories?.l2_Category?.name
      );
      cartListElements.categoryLevel3List = pushIfValid(
        cartListElements.categoryLevel3List,
        categories?.l3_Category?.name
      );
      cartListElements.categoryLevel4List = pushIfValid(
        cartListElements.categoryLevel4List,
        categories?.l4_Category?.name
      );

      if (offersCount > 0) {
        const offerData = `${item.productId}:${offers.join('|')}`;
        cartListElements.cartAvailableOffers.push(offerData);
      }

      if (appliedOffers.length > 0) {
        const offerData = `${item.productId}:${appliedOffers.join('|')}`;
        cartListElements.cartAppliedOffers.push(offerData);
      }

      const productStockStatus = item.isOutOfStock
        ? 'OOS'
        : isPartialOOS(item)
          ? 'partialOOS'
          : 'instock';

      cartListElements.cartProducts.push({
        edd_o2d: item?.estimatedDeliveryTime,
        product_id: item?.parentId,
        product_name: item?.productTitle,
        product_type: item?.productType,
        mrp_price: item?.cartItemPriceDTO?.mrp,
        product_stock_status: productStockStatus,
        brand_name: item?.brandNames[0],
        discounted_percent: item?.cartItemPriceDTO?.catalogDiscountRate,
        discounted_price: item?.cartItemPriceDTO?.price,
        is_edd_present: item?.estimatedDeliveryTime ? true : false,
        quantity: item?.quantity,
        variant_product_id: item?.productId,
        variant_type: item?.packSize,
        is_checkbox_selected:
          productStockStatus === 'instock' ? item?.selectedItemForCheckout : false,
        is_price_participating_sku: item?.onSale === true ? true : false,
        catalog_tags: item?.catalogTag,
        store_selected: item?.trackingParam?.store || buildStoreRedirectId(),
        return_nudge_applicable: item?.trackingParam?.return_nudge_applicable || false,
        ...(item?.onSale === true
          ? {
            is_price_participating_sku_list: (() => {
              switch (item?.tag) {
                case 1:
                  return 'MRP/DP';
                case 2:
                  return 'CART Rule';
                case 3:
                  return 'Both';
                default:
                  return '';
              }
            })()
          }
          : {})
      });
    });

    unselectedItems.forEach((item) => {
      const appliedOffers = item.offerCommunication?.appliedOfferIds || [];
      const offers = item.offerCommunication?.availableOfferIds || [];
      const offersCount = offers.length;

      // this is added to support for future, currently we are not getting this value
      const categories = item?.primary_categories_json_all || undefined;

      const isLuxeUnselectedItem = item?.catalogTag?.includes('fashion_luxe') ?? false;
      if (isLuxeUnselectedItem) luxeItemCount++;
      totalIteratedItems++;

      cartElements.availableOffersCount += offersCount;
      cartElements.productWithOfferCount += offersCount > 0 ? 1 : 0;
      cartElements.cartOOSCount += item.isOutOfStock ? 1 : 0;
      cartElements.cartPartialOOSCount += isPartialOOS(item) ? 1 : 0;
      cartElements.cartInStockCount += item.isOutOfStock || isPartialOOS(item) ? 0 : 1;
      if (cart?.isEligibleCartPartialCheckout) {
        cartElements.checkboxUnselectedCount += isPartialOOS(item) ? 0 : 1;
      }

      cartListElements.brandNameList = pushIfValid(
        cartListElements.brandNameList,
        item?.brandNames[0]
      );
      cartListElements.productIdList.push(item.parentId);
      cartListElements.productNameList.push(item.name);
      cartListElements.variantProductIdList.push(item.productId);

      cartListElements.productTypeList = pushIfValid(
        cartListElements.productTypeList,
        item.productType
      );
      cartListElements.categoryLevel1List = pushIfValid(
        cartListElements.categoryLevel1List,
        categories?.l1_Category?.name
      );
      cartListElements.categoryLevel2List = pushIfValid(
        cartListElements.categoryLevel2List,
        categories?.l2_Category?.name
      );
      cartListElements.categoryLevel3List = pushIfValid(
        cartListElements.categoryLevel3List,
        categories?.l3_Category?.name
      );
      cartListElements.categoryLevel4List = pushIfValid(
        cartListElements.categoryLevel4List,
        categories?.l4_Category?.name
      );

      if (offersCount > 0) {
        const offerData = `${item.productId}:${offers.join('|')}`;
        cartListElements.cartAvailableOffers.push(offerData);
      }

      if (appliedOffers.length > 0) {
        const offerData = `${item.productId}:${appliedOffers.join('|')}`;
        cartListElements.cartAppliedOffers.push(offerData);
      }

      if (cart?.isEligibleCartPartialCheckout) {
        cartElements.checkboxTotalCount += item.isOutOfStock || isPartialOOS(item) ? 0 : 1;
      }

      const productStockStatus = item.isOutOfStock
        ? 'OOS'
        : isPartialOOS(item)
          ? 'partialOOS'
          : 'instock';

      cartListElements.cartProducts.push({
        edd_o2d: item?.estimatedDeliveryTime,
        product_id: item?.parentId,
        product_name: item?.productTitle,
        product_type: item?.productType,
        mrp_price: item?.cartItemPriceDTO?.mrp,
        product_stock_status: productStockStatus,
        brand_name: item?.brandNames[0],
        discounted_percent: item?.cartItemPriceDTO?.catalogDiscountRate,
        discounted_price: item?.cartItemPriceDTO?.price,
        is_edd_present: item?.estimatedDeliveryTime ? true : false,
        quantity: item?.quantity,
        variant_product_id: item?.productId,
        variant_type: item?.packSize,
        is_checkbox_selected: false,
        is_price_participating_sku: item?.onSale === true ? true : false,
        catalog_tags: item?.catalogTag,
        store_selected: item?.trackingParam?.store || 'fashion_web',
        return_nudge_applicable: item?.trackingParam?.return_nudge_applicable || false,
        ...(item?.onSale === true
          ? {
            is_price_participating_sku_list: (() => {
              switch (item?.tag) {
                case 1:
                  return 'MRP/DP';
                case 2:
                  return 'CART Rule';
                case 3:
                  return 'Both';
                default:
                  return '';
              }
            })()
          }
          : {})
      });
    });

    const { cartProducts, ...listElementsToStringify } = cartListElements;

    return {
      ...cartElements,
      cartProducts: cartProducts,
      checkboxCheckUnCheckCount: getCheckUnCheckCounter(),
      partialCheckingOutCart: totalItems.length > 0 && cart?.unselectedItems?.length > 0,
      offerMetaDetail: cart?.appliedOfferLabelDetails
        ? JSON.stringify(cart?.appliedOfferLabelDetails)
        : '[]',
      ...getListToString(listElementsToStringify),
      isOnSalePresentInTopN: isOnSalePresentInTopN,
      specialTagEnabled: isSpecialTagsPresent,
      ...(isSpecialTagsPresent ? { specialTagNamesList: specialTagsNameList.join(',') } : {}),
      contains_luxe_items: luxeItemCount > 0,
      luxe_item_count: luxeItemCount,
      cart_type: luxeItemCount === 0 ? 'fashion_only' : luxeItemCount === totalIteratedItems ? 'luxe_only' : 'mixed',
    };
  } catch (err) {
    return {};
  }
};

export const setStorageEddVariant = (isEddV2Enabled = false) => {
  try {
    if (window.sessionStorage === undefined) {
      return;
    }
    window.sessionStorage.setItem(LOCAL_STORAGE_KEY_EDD, isEddV2Enabled ? 'true' : 'false');
  } catch (e) {
    /** DO NOTHING */
  }
};
